import { MultiSelect } from '@/components/ui/multi-select';
import { FOLDER_PURPOSES, FOLDER_PURPOSE_LABELS, folderPurposeOf, purposeFilterOf } from '@/lib/folderPurpose';
import { PresenterCheckButton, PRESENTER_OPTIONS } from '../components/PresenterCheckButton';
import { useMemo, useState, type JSX } from 'react';
import { toast } from 'sonner';
import { EyeOff, FolderPlus, Loader2, RefreshCw, Send, Trash2, Undo2, UserRoundCog } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUrlState } from '@/hooks/useUrlState';
import { useDiscoveryClient } from '../api/context';
import { PLATFORM_LABELS, PLATFORMS, type Platform } from '../lib/platform';
import {
  useCreatorCounts,
  useCreatorRegistry,
  useRefreshRegistryStatuses,
  useRemoveFromAllFolders,
  useSuppressCreator,
  useUnsuppressCreator,
} from '../hooks/registry';
import { useFolders, foldersKey } from '../hooks/folders';
import { leadsKey } from '../hooks/leads';
import { useQueryClient } from '@tanstack/react-query';
import { OUTREACH_STATE_ORDER, outreachStateLabel } from '../components/OutreachStatus';
import { LeadTable, BulkActions, type BulkAction } from '../components/lead-table/LeadTable';
import { CreatorSheet, type SheetAction } from '../components/lead-table/CreatorSheet';
import { fromCreator, type LeadRow } from '../components/lead-table/rows';
import { emptySelection, filterKeyOf, forFilter, selectedCount, type SelectionState } from '../components/lead-table/selection';
import { ReassignDialog, SendToOutreachDialog, type BulkTarget } from '../workspace/AssignDialogs';
import { collectAll } from '../workspace/hooks';
import { DebouncedInput, Field, Note } from '../workspace/ui';
import type { CreatorAssignmentFields, CreatorListFilters, LeadEnrichment, OutreachState } from '../api/types';

const PAGE_SIZE = 50;

const URL_DEFAULTS = { purpose: '', folders: '', presenter: '', q: '', platform: '', state: '', folder: '', email: 'any', suppressed: false, page: 1 };

/**
 * Every creator this team has discovered — once, with the folders they sit in
 * and where they are in outreach. Same lead table as a folder's Leads tab,
 * with bulk Add to folder / Send to outreach / Reassign.
 */
export function CreatorsScreen(): JSX.Element {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  const [url, setUrl] = useUrlState(URL_DEFAULTS);
  const set = (patch: Partial<typeof URL_DEFAULTS>) => setUrl({ ...patch, page: 1 });

  const filter: CreatorListFilters = useMemo(
    () => ({
      ...(url.presenter ? { presenter: url.presenter as CreatorListFilters['presenter'] } : {}),
      ...(url.q.trim() ? { search: url.q.trim() } : {}),
      ...(url.state ? { outreachState: url.state as OutreachState } : {}),
      ...((url.folders || url.folder) ? { folderIds: url.folders || url.folder } : {}),
      ...(purposeFilterOf(url.purpose) ? { folderPurpose: purposeFilterOf(url.purpose) || undefined } : {}),
      ...(url.email !== 'any' ? { hasEmail: url.email === 'yes' } : {}),
      ...(url.platform ? { platform: url.platform as Platform } : {}),
      ...(url.suppressed ? {} : { suppressed: false }),
    }),
    [url.presenter, url.q, url.state, url.folder, url.folders, url.purpose, url.email, url.platform, url.suppressed],
  );
  const listQuery = useMemo(
    () => ({ ...filter, limit: PAGE_SIZE, offset: (Math.max(1, url.page) - 1) * PAGE_SIZE }),
    [filter, url.page],
  );
  const { data, isFetching, error } = useCreatorRegistry(listQuery);
  const counts = useCreatorCounts().data;
  const folders = useFolders({ includeArchived: true }).data ?? [];
  const purpose = purposeFilterOf(url.purpose);
  const selectedFolderIds = (url.folders || url.folder).split(',').filter(Boolean);
  const visibleFolders = folders.filter((f) => !purpose || folderPurposeOf(f.purpose) === purpose);
  const refresh = useRefreshRegistryStatuses();
  const suppress = useSuppressCreator();
  const unsuppress = useUnsuppressCreator();
  const removeAll = useRemoveFromAllFolders();

  const rows = useMemo(
    () => (data?.creators ?? []).map((c) => fromCreator(c as typeof c & CreatorAssignmentFields)),
    [data],
  );
  const total = data?.total ?? 0;

  const filterKey = filterKeyOf(filter);
  const [rawSel, setSelection] = useState<SelectionState<LeadRow>>(() => emptySelection(filterKey));
  const selection = forFilter(rawSel, filterKey);
  const selCount = selectedCount(selection, total);
  const clearSelection = () => setSelection(emptySelection(filterKey));

  const [dialog, setDialog] = useState<null | 'send' | 'reassign' | 'addToFolder' | 'removeAll'>(null);
  const [sheetTarget, setSheetTarget] = useState<BulkTarget | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const openIndex = rows.findIndex((r) => r.key === openKey);
  const openRow = openIndex >= 0 ? rows[openIndex]! : null;

  const rowsTarget = (list: LeadRow[]): BulkTarget => ({
    kind: 'rows',
    count: list.length,
    usernames: list.map((r) => r.handleKey),
    leadIds: [],
    creatorIds: list.map((r) => r.creatorId),
  });
  const target: BulkTarget =
    sheetTarget ?? (selection.mode === 'all' ? { kind: 'all', count: total, filter } : rowsTarget([...selection.rows.values()]));

  /** Every selected creator, fetching every page when "all matching" is selected. */
  async function selectedRows(): Promise<LeadRow[]> {
    if (selection.mode === 'rows') return [...selection.rows.values()];
    const all = await collectAll(async (page, limit) => {
      const r = await client.registry.list({ ...filter, limit, offset: (page - 1) * limit });
      return { rows: r.creators, total: r.total };
    });
    return all.map((c) => fromCreator(c));
  }

  const bulkActions: BulkAction[] = [
    { label: 'Add to folder…', icon: FolderPlus, onClick: () => setDialog('addToFolder') },
    { label: 'Send to outreach', icon: Send, onClick: () => setDialog('send') },
    { label: 'Reassign…', icon: UserRoundCog, onClick: () => setDialog('reassign') },
  ];

  const sheetActions: SheetAction[] = openRow
    ? [
        { label: 'Send to outreach', icon: Send, primary: true, onClick: () => { setSheetTarget(rowsTarget([openRow])); setDialog('send'); } },
        { label: 'Reassign…', icon: UserRoundCog, onClick: () => { setSheetTarget(rowsTarget([openRow])); setDialog('reassign'); } },
        openRow.suppressedAt
          ? { label: 'Unsuppress', icon: Undo2, disabled: unsuppress.isPending, onClick: () => unsuppress.mutate(openRow.handleKey, { onSuccess: () => toast.success(`@${openRow.username} can turn up in searches again`) }) }
          : { label: 'Suppress', icon: EyeOff, disabled: suppress.isPending, onClick: () => suppress.mutate({ username: openRow.handleKey }, { onSuccess: () => toast.success(`@${openRow.username} won't turn up in searches`) }) },
        ...(openRow.folderNames.length > 0
          ? [{ label: `Remove from ${openRow.folderNames.length} folder${openRow.folderNames.length === 1 ? '' : 's'}`, icon: Trash2, destructive: true, onClick: () => setDialog('removeAll') }]
          : []),
      ]
    : [];

  const closeDialog = (open: boolean) => {
    if (!open) {
      setDialog(null);
      setSheetTarget(null);
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-0.03em]">Creators</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every creator your team has discovered, listed once — {total.toLocaleString()} match.
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
        <PresenterCheckButton initialFolderIds={selectedFolderIds} initialPurpose={purpose} />
        <Button
          variant="outline"
          size="sm"
          disabled={rows.length === 0 || refresh.isPending}
          onClick={() =>
            refresh.mutate(rows.map((r) => r.handleKey), {
              onSuccess: (r) => toast.success(`Checked ${rows.length} creators`, { description: `${r.updated} changed status.` }),
            })
          }
          title="Re-check outreach and platform status for the creators on this page"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', refresh.isPending && 'animate-spin')} />
          Refresh status
        </Button>
        </div>
      </header>

      {counts && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {OUTREACH_STATE_ORDER.map((st) => {
            const isActive = url.state === st;
            return (
              <button
                key={st}
                type="button"
                aria-pressed={isActive}
                onClick={() => set({ state: isActive ? '' : st })}
                className={cn(
                  'panel rounded-xl px-3 py-2.5 text-left transition-colors',
                  isActive ? 'border-brand/50 bg-brand/10' : 'hover:bg-accent/40',
                )}
              >
                <div className={cn('font-mono text-lg font-semibold tabular-nums', isActive && 'text-brand')}>
                  {(counts[st] ?? 0).toLocaleString()}
                </div>
                <div className="truncate text-sm text-muted-foreground">{outreachStateLabel(st)}</div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Search" className="min-w-[12rem] flex-1">
          <DebouncedInput placeholder="@username or name" value={url.q} onCommit={(q) => set({ q })} />
        </Field>
        <Field label="Platform">
          <Combobox
            className="w-full md:w-36"
            value={url.platform}
            onChange={(v) => set({ platform: v })}
            allowAll
            allLabel="All platforms"
            options={PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
          />
        </Field>
        <Field label="Outreach">
          <Combobox
            className="w-full md:w-44"
            value={url.state}
            onChange={(v) => set({ state: v })}
            allowAll
            allLabel="Any status"
            options={OUTREACH_STATE_ORDER.map((s) => ({ value: s, label: outreachStateLabel(s) }))}
          />
        </Field>
        <Field label="Folder type">
          <Combobox className="w-full md:w-40" value={purpose} onChange={(purpose) => set({ purpose, folders: '', folder: '' })}
            allowAll allLabel="All types" options={FOLDER_PURPOSES.map((p) => ({ value: p, label: FOLDER_PURPOSE_LABELS[p] }))} />
        </Field>
        <Field label="Folders">
          <MultiSelect className="w-full md:w-56" value={selectedFolderIds} onChange={(ids) => set({ folders: ids.join(','), folder: '' })}
            options={visibleFolders.map((f) => ({ value: f.publicId, label: `${f.name} · ${FOLDER_PURPOSE_LABELS[folderPurposeOf(f.purpose)]}` }))} placeholder="All matching folders" />
        </Field>
        <Field label="On camera">
          <Combobox className="w-full md:w-40" value={url.presenter} onChange={(presenter) => set({ presenter })} allowAll allLabel="All creators" options={PRESENTER_OPTIONS} />
        </Field>
        <Field label="Email">
          <Combobox
            className="w-full md:w-32"
            value={url.email === 'any' ? '' : url.email}
            onChange={(v) => set({ email: v || 'any' })}
            allowAll
            allLabel="Any"
            options={[
              { value: 'yes', label: 'Has email' },
              { value: 'no', label: 'No email' },
            ]}
          />
        </Field>
        <label className="mb-2 inline-flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--brand)]"
            checked={url.suppressed}
            onChange={(ev) => set({ suppressed: ev.target.checked })}
          />
          <span className={url.suppressed ? 'text-foreground' : 'text-muted-foreground'}>Show suppressed</span>
        </label>
      </div>

      <LeadTable
        scope="creators"
        tableId="discovery-creators-v1"
        noun="creator"
        rows={rows}
        total={total}
        loading={isFetching}
        error={error as Error | null}
        page={Math.max(1, url.page)}
        limit={PAGE_SIZE}
        onPageChange={(page) => setUrl({ page })}
        selection={selection}
        onSelectionChange={setSelection}
        onOpenRow={(row) => setOpenKey(row.key)}
        bulkActions={<BulkActions actions={bulkActions} />}
        emptyTitle="No creators match"
        emptyHint="Try widening the filters, or run a search to fill a folder."
      />

      <CreatorSheet
        row={openRow}
        open={!!openRow}
        onOpenChange={(o) => !o && setOpenKey(null)}
        onPrev={openIndex > 0 ? () => setOpenKey(rows[openIndex - 1]!.key) : undefined}
        onNext={openIndex >= 0 && openIndex < rows.length - 1 ? () => setOpenKey(rows[openIndex + 1]!.key) : undefined}
        actions={sheetActions}
      />

      <SendToOutreachDialog
        open={dialog === 'send'}
        onOpenChange={closeDialog}
        scope={{ kind: 'creators' }}
        target={target}
        onDone={sheetTarget ? undefined : clearSelection}
      />
      <ReassignDialog
        open={dialog === 'reassign'}
        onOpenChange={closeDialog}
        scope={{ kind: 'creators' }}
        target={target}
        onDone={sheetTarget ? undefined : clearSelection}
      />
      <AddToFolderDialog
        open={dialog === 'addToFolder'}
        onOpenChange={closeDialog}
        count={selCount}
        folders={folders.filter((f) => !f.archivedAt).map((f) => ({ value: f.publicId, label: f.name }))}
        onAdd={async (folderPublicId) => {
          const list = await selectedRows();
          const creators: LeadEnrichment[] = list.map((r) => ({ ...(r.enrichment ?? {}), username: r.username, platform: r.platform }));
          const res = await client.leads.saveBatch(folderPublicId, creators);
          void qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) });
          void qc.invalidateQueries({ queryKey: foldersKey });
          void qc.invalidateQueries({ queryKey: ['discovery', 'registry'] });
          toast.success(`Added ${res.saved.toLocaleString()} to "${folders.find((f) => f.publicId === folderPublicId)?.name ?? 'folder'}"`);
          clearSelection();
        }}
      />
      {openRow && (
        <ConfirmDialog
          open={dialog === 'removeAll'}
          onOpenChange={closeDialog}
          tone="destructive"
          title={`Remove @${openRow.username} from every folder?`}
          description={`They come out of ${openRow.folderNames.length} folder${openRow.folderNames.length === 1 ? '' : 's'}. Outreach and suppression aren't touched.`}
          confirmLabel="Remove from all folders"
          pending={removeAll.isPending}
          onConfirm={() =>
            removeAll.mutateAsync(openRow.handleKey).then((r) => {
              toast.success(`Removed from ${r.removed} folder${r.removed === 1 ? '' : 's'}`);
            })
          }
        />
      )}
    </div>
  );
}

function AddToFolderDialog({
  open,
  onOpenChange,
  count,
  folders,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  folders: { value: string; label: string }[];
  onAdd: (folderPublicId: string) => Promise<void>;
}): JSX.Element {
  const [folder, setFolder] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setPending(true);
    setError(null);
    try {
      await onAdd(folder);
      onOpenChange(false);
      setFolder('');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add them");
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Add {count.toLocaleString()} creator{count === 1 ? '' : 's'} to a folder
          </DialogTitle>
          <DialogDescription>They stay in their other folders too. Nothing is searched again.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-1.5">
          <Label>Folder</Label>
          <Combobox value={folder} onChange={setFolder} options={folders} placeholder="Pick a folder…" emptyText="No folders" />
        </div>
        {error && <Note tone="error">{error}</Note>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!folder || pending || count === 0} onClick={() => void submit()}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            Add to folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
